/* main.js - cleaned & modularized
   Keep the same markup/classes as before. Uses GSAP + ScrollTrigger.
*/
(() => {
  "use strict";

  /* -------------------------
     Small utilities
     ------------------------- */
  const $ = (sel, ctx = window.document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = window.document) =>
    Array.from(ctx.querySelectorAll(sel));
  const safe = (fn) => {
    try {
      return fn();
    } catch (e) {
      // swallow occasional DOM throws
      return undefined;
    }
  };

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  const htmlEl = document.documentElement;
  const bodyEl = document.body;
  const prevScrollState = {
    htmlOverflow: "",
    bodyOverflow: "",
    htmlScrollBehavior: "",
  };
  let scrollLockActive = false;

  const forceScrollTop = () => {
    window.scrollTo(0, 0);
    if (htmlEl) htmlEl.scrollTop = 0;
    if (bodyEl) bodyEl.scrollTop = 0;
  };

  const lockScrollUntilReady = () => {
    if (!htmlEl || !bodyEl || scrollLockActive) return;
    scrollLockActive = true;
    prevScrollState.htmlOverflow = htmlEl.style.overflow;
    prevScrollState.bodyOverflow = bodyEl.style.overflow;
    prevScrollState.htmlScrollBehavior = htmlEl.style.scrollBehavior;
    htmlEl.style.overflow = "hidden";
    bodyEl.style.overflow = "hidden";
    htmlEl.style.scrollBehavior = "auto";
    forceScrollTop();
  };

  const releaseScrollLock = () => {
    if (!htmlEl || !bodyEl || !scrollLockActive) return;
    scrollLockActive = false;
    htmlEl.style.overflow = prevScrollState.htmlOverflow || "";
    bodyEl.style.overflow = prevScrollState.bodyOverflow || "";
    if (prevScrollState.htmlScrollBehavior) {
      htmlEl.style.scrollBehavior = prevScrollState.htmlScrollBehavior;
    } else {
      htmlEl.style.removeProperty("scroll-behavior");
    }
    forceScrollTop();
  };

  const releaseScrollLockWhenReady = () => {
    if (!scrollLockActive) return;
    if (window.ScrollTrigger) {
      const finish = () => {
        ScrollTrigger.removeEventListener("refresh", finish);
        releaseScrollLock();
      };
      ScrollTrigger.addEventListener("refresh", finish);
      try {
        ScrollTrigger.refresh();
      } catch (e) {
        finish();
      }
    } else {
      requestAnimationFrame(() => releaseScrollLock());
    }
  };

  // Only lock scroll on desktop devices
  if (window.innerWidth >= 768) {
    lockScrollUntilReady();
  }

  // small debounce (used instead of relying on gsap.utils.debounce)
  const debounce = (fn, wait = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  /* -------------------------
     Small DOM helpers for unmute buttons
     ------------------------- */
  const isTouchDevice = () =>
    /Mobi|Android/i.test(navigator.userAgent) || "ontouchstart" in window;

  const createUnmuteButton = (videoEl) => {
    if (!videoEl) return null;
    const wrapper =
      videoEl.closest(".taskforce-section-panel-content-media-wrapper") ||
      videoEl.parentElement;
    if (!wrapper) return null;
    if (wrapper.querySelector(".taskforce-unmute"))
      return wrapper.querySelector(".taskforce-unmute");

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "taskforce-unmute";
    btn.dataset.unmuteLabel = isTouchDevice()
      ? "Tap to play sound"
      : "Click to play sound";
    btn.dataset.muteLabel = "Mute video";

    const setIcon = (isMuted) => {
      const svgMuted = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000" class="bi bi-volume-mute" viewBox="0 0 16 16"><path d="M6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06M6 5.04 4.312 6.39A.5.5 0 0 1 4 6.5H2v3h2a.5.5 0 0 1 .312.11L6 10.96zm7.854.606a.5.5 0 0 1 0 .708L12.207 8l1.647 1.646a.5.5 0 0 1-.708.708L11.5 8.707l-1.646 1.647a.5.5 0 0 1-.708-.708L10.793 8 9.146 6.354a.5.5 0 1 1 .708-.708L11.5 7.293l1.646-1.647a.5.5 0 0 1 .708 0"/></svg>`;
      const svgUnmuted = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000" class="bi bi-volume-up" viewBox="0 0 16 16"><path d="M11.536 14.01A8.47 8.47 0 0 0 14.026 8a8.47 8.47 0 0 0-2.49-6.01l-.708.707A7.48 7.48 0 0 1 13.025 8c0 2.071-.84 3.946-2.197 5.303z"/><path d="M10.121 12.596A6.48 6.48 0 0 0 12.025 8a6.48 6.48 0 0 0-1.904-4.596l-.707.707A5.48 5.48 0 0 1 11.025 8a5.48 5.48 0 0 1-1.61 3.89z"/><path d="M10.025 8a4.5 4.5 0 0 1-1.318 3.182L8 10.475A3.5 3.5 0 0 0 9.025 8c0-.966-.392-1.841-1.025-2.475l.707-.707A4.5 4.5 0 0 1 10.025 8M7 4a.5.5 0 0 0-.812-.39L3.825 5.5H1.5A.5.5 0 0 0 1 6v4a.5.5 0 0 0 .5.5h2.325l2.363 1.89A.5.5 0 0 0 7 12zM4.312 6.39 6 5.04v5.92L4.312 9.61A.5.5 0 0 0 4 9.5H2v-3h2a.5.5 0 0 0 .312-.11"/></svg>`;
      btn.innerHTML = isMuted ? svgMuted : svgUnmuted;
      btn.setAttribute(
        "aria-label",
        isMuted ? btn.dataset.unmuteLabel : btn.dataset.muteLabel
      );
    };

    setIcon(videoEl.muted !== false);
    // ensure wrapper is positioned so overlay can be absolutely placed
    const cs = getComputedStyle(wrapper);
    if (cs.position === "static") wrapper.style.position = "relative";
    wrapper.appendChild(btn);
    btn.classList.add("hidden");

    // toggle mute
    btn.addEventListener("click", () => {
      if (videoEl.muted !== false) {
        videoEl.muted = false;
        videoEl.removeAttribute("muted");
        safe(() => videoEl.play());
        setIcon(false);
      } else {
        videoEl.muted = true;
        videoEl.setAttribute("muted", "");
        setIcon(true);
      }
    });

    videoEl.dataset.unmuteBound = "true";
    return btn;
  };

  /* -------------------------
     Mobile specific adjustments
     ------------------------- */
  const mobileFallback = () => {
    if (!window.matchMedia("(max-width: 767.9px)").matches) return;

    // if ScrollTrigger/gsap present, aggressively kill heavy animations on small screens
    if (window.ScrollTrigger) {
      try {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        ScrollTrigger.killAll();
      } catch (e) {}
    }
    if (window.gsap) {
      try {
        gsap.globalTimeline.clear();
        gsap.globalTimeline.pause();
        gsap.killTweensOf("*");
      } catch (e) {}
    }
    // stop further heavy execution
    return true;
  };

  /* -------------------------
     Banner animation & pin
     ------------------------- */
  const initBanner = () => {
    const bannerSection = document.querySelector(".banner-section");
    if (!bannerSection) return;

    const ghNavigationInner = $(".gh-navigation-inner");
    const bannerSectionTextContent = $$(".banner-section-text-content > *");
    const heroImageLeft = $(".hero-image-left");
    const heroImageRight = $(".hero-image-right");
    const heroImageMain = $(".hero-image-main");
    const whyAxariText = $$(".why-axari-text > *");
    const whyAxariImages = $$(".why-axari-images img");
    const whyAxariContainer = $(".why-axari-container");
    const bannerSectionBackground = $(".banner-section-background");
    const whyAxariMask = $(".why-axari-mask");
    const mainImageMask = $(".main-image-mask");
    const whyAxariBackgroundMask = $(".why-axari-background-mask");
    const whyAxariVideo = $(".why-axari-animated-video");

    gsap.set(ghNavigationInner, { y: -50, opacity: 0 });
    gsap.set(bannerSectionTextContent, { y: 50, opacity: 0 });
    gsap.set(heroImageLeft, { x: 100, opacity: 0 });
    gsap.set(heroImageRight, { x: -100, opacity: 0 });
    gsap.set(heroImageMain, { y: 100, scale: 0.9, opacity: 0 });
    gsap.set(whyAxariText, { y: 50, opacity: 0 });
    gsap.set(whyAxariImages, {
      opacity: 0,
      scale: 0.5,
    });
    gsap.set(whyAxariVideo, { opacity: 0 });

    const intro = gsap.timeline({ defaults: { ease: "power3.out" } });

    intro
      .to(
        ghNavigationInner,
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
        },
        "+=0.3"
      )
      .to(
        heroImageMain,
        {
          y: 0,
          scale: 1,
          opacity: 1,
          duration: 0.5,
        },
        "-=0.3"
      )
      .to(
        heroImageLeft,
        {
          x: 0,
          opacity: 1,
          duration: 1,
        },
        "-=0.3"
      )
      .to(
        heroImageRight,
        {
          x: 0,
          opacity: 1,
          duration: 1,
        },
        "-=1"
      )
      .to(
        bannerSectionTextContent,
        {
          y: 0,
          opacity: 1,
          stagger: 0.15,
          duration: 0.8,
        },
        "-=0.5"
      );

    intro.eventCallback("onComplete", () => {
      const maskTarget = mainImageMask || whyAxariMask;

      const ensureVideoStartsPaused = (videoEl) => {
        if (!videoEl) return;
        const forcePause = () => {
          try {
            videoEl.pause();
            videoEl.currentTime = 0;
          } catch (e) {}
        };

        try {
          videoEl.removeAttribute("autoplay");
          videoEl.autoplay = false;
          forcePause();
          videoEl.addEventListener("loadedmetadata", forcePause, {
            once: true,
          });
          videoEl.addEventListener("loadeddata", forcePause, { once: true });
        } catch (e) {}
      };

      ensureVideoStartsPaused(whyAxariVideo);

      const computeMainImagePlacement = () => {
        if (!heroImageMain || !maskTarget) {
          const fallbackWidth = heroImageMain ? heroImageMain.offsetWidth : 0;
          const fallbackHeight = heroImageMain ? heroImageMain.offsetHeight : 0;
          const fallbackLeft = heroImageMain ? heroImageMain.offsetLeft : 0;
          const fallbackTop = heroImageMain ? heroImageMain.offsetTop : 0;
          return {
            width: fallbackWidth,
            height: fallbackHeight,
            relativeX: fallbackLeft,
            relativeY: fallbackTop,
          };
        }

        const maskRect = maskTarget.getBoundingClientRect();
        const parentRect = heroImageMain.parentElement
          ? heroImageMain.parentElement.getBoundingClientRect()
          : { left: 0, top: 0 };

        const width = maskRect.width;
        const height = maskRect.height;

        const relativeX = maskRect.left - parentRect.left;
        const relativeY = maskRect.top - parentRect.top;

        return {
          width,
          height,
          relativeX,
          relativeY,
        };
      };

      const placement = () => computeMainImagePlacement();

      const computeBannerBackgroundPlacement = () => {
        const target = whyAxariBackgroundMask || maskTarget;
        if (!bannerSectionBackground || !target) {
          const fallbackWidth = bannerSectionBackground
            ? bannerSectionBackground.offsetWidth
            : 0;
          const fallbackHeight = bannerSectionBackground
            ? bannerSectionBackground.offsetHeight
            : 0;
          const fallbackLeft = bannerSectionBackground
            ? bannerSectionBackground.offsetLeft
            : 0;
          const fallbackTop = bannerSectionBackground
            ? bannerSectionBackground.offsetTop
            : 0;
          return {
            width: fallbackWidth,
            height: fallbackHeight,
            relativeX: fallbackLeft,
            relativeY: fallbackTop,
          };
        }

        const maskRect = target.getBoundingClientRect();
        const parentRect = bannerSectionBackground.offsetParent
          ? bannerSectionBackground.offsetParent.getBoundingClientRect()
          : bannerSectionBackground.parentElement
          ? bannerSectionBackground.parentElement.getBoundingClientRect()
          : { left: 0, top: 0 };

        return {
          width: maskRect.width,
          height: maskRect.height,
          relativeX: maskRect.left - parentRect.left,
          relativeY: maskRect.top - parentRect.top,
        };
      };

      const backgroundPlacement = () => computeBannerBackgroundPlacement();

      gsap.set(
        [heroImageLeft, heroImageRight, heroImageMain, bannerSectionBackground],
        { clearProps: "transform" }
      );

      const heroLeftImage = gsap.to(heroImageLeft, {
        xPercent: 100,
        opacity: 0,
        duration: 0.5,
        paused: true,
        ease: "power2.out",
      });

      const heroRightImage = gsap.to(heroImageRight, {
        xPercent: 100,
        opacity: 0,
        duration: 0.8,
        paused: true,
        ease: "power2.out",
      });

      const mainImage = gsap.to(heroImageMain, {
        x: 0,
        y: 0,
        left: () => {
          const { relativeX, width } = placement();
          const extraShift = Math.min(width * 0.04, 20);
          return `${relativeX - extraShift}px`;
        },
        top: () => `${placement().relativeY}px`,
        width: () => `${placement().width}px`,
        height: () => `${placement().height}px`,
        scale: 0.85,
        transformOrigin: "100% 100%",
        duration: 0.6,
        ease: "power2.out",
        paused: true,
      });

      const bannerBackground = gsap.to(bannerSectionBackground, {
        x: 0,
        y: 0,
        left: () => `${backgroundPlacement().relativeX}px`,
        top: () => `${backgroundPlacement().relativeY}px`,
        width: () => `${backgroundPlacement().width}px`,
        height: () => `${backgroundPlacement().height}px`,
        duration: 0.8,
        borderRadius: "7.5%",
        ease: "power2.out",
        paused: true,
      });

      const bannerSectionText = gsap.to(bannerSectionTextContent, {
        opacity: 0,
        duration: 0.2,
        ease: "power2.out",
        paused: true,
      });

      const whyAxariTextTimeline = gsap.to(whyAxariText, {
        y: 0,
        opacity: 1,
        stagger: 0.1,
        duration: 0.5,
        ease: "power2.out",
        paused: true,
      });

      if (whyAxariContainer) gsap.set(whyAxariContainer, { zIndex: 0 });

      const staffAndVideoReveal = gsap.timeline({ paused: true });

      staffAndVideoReveal.to(whyAxariImages, {
        opacity: 1,
        scale: 1,
        transform: "translate(0%, 0%)",
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.05,
      });

      staffAndVideoReveal.add(() => {
        if (whyAxariVideo) {
          ensureVideoStartsPaused(whyAxariVideo);
          whyAxariVideo.play().catch(() => {});
        }
      });

      staffAndVideoReveal.to(whyAxariVideo, {
        opacity: 1,
        duration: 0.25,
        ease: "power2.out",
      });

      staffAndVideoReveal.eventCallback("onReverseComplete", () => {
        if (whyAxariVideo) {
          ensureVideoStartsPaused(whyAxariVideo);
        }
      });

      if (whyAxariContainer) {
        let whyAxariZTimeout = null;
        const applyZIndex = (value, delay = 0) => {
          if (whyAxariZTimeout) {
            clearTimeout(whyAxariZTimeout);
            whyAxariZTimeout = null;
          }
          if (delay > 0) {
            whyAxariZTimeout = setTimeout(() => {
              whyAxariContainer.style.zIndex = value;
              whyAxariZTimeout = null;
            }, delay);
          } else {
            whyAxariContainer.style.zIndex = value;
          }
        };

        const syncWhyAxariZIndex = () => {
          const active = staffAndVideoReveal.progress() > 0;
          const reversed = staffAndVideoReveal.reversed();
          if (active && !reversed) {
            applyZIndex("5", 250);
          } else {
            applyZIndex("0", 0);
          }
        };

        staffAndVideoReveal.eventCallback("onUpdate", syncWhyAxariZIndex);
        staffAndVideoReveal.eventCallback("onComplete", syncWhyAxariZIndex);
        syncWhyAxariZIndex();
      }

      mainImage.eventCallback("onComplete", () => {
        staffAndVideoReveal.play();
        whyAxariTextTimeline.play();
      });
      // Create pin + direction-based toggle
      ScrollTrigger.create({
        trigger: bannerSection,
        start: "top top-=1",
        end: "+=50%",
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        refreshPriority: 2,
        onLeave: () => {},
        onLeaveBack: () => {},
        onUpdate: (self) => {
          if (self.direction === 1) {
            heroLeftImage.play();
            heroRightImage.play();
            mainImage.play();
            bannerBackground.play();
            bannerSectionText.play();
            if (mainImage.progress() === 1) {
              staffAndVideoReveal.play();
            }
          } else {
            const toReverse = [
              staffAndVideoReveal,
              mainImage,
              heroRightImage,
              heroLeftImage,
              whyAxariTextTimeline,
              bannerBackground,
            ].filter((t) => t && t.progress && t.progress() > 0);
            if (toReverse.length === 0) {
              bannerSectionText.reverse();
            } else {
              let pending = toReverse.length;
              toReverse.forEach((t) => {
                t.eventCallback("onReverseComplete", () => {
                  t.eventCallback("onReverseComplete", null);
                  pending--;
                  if (pending === 0) {
                    bannerSectionText.reverse();
                  }
                });
                t.reverse();
              });
            }
          }
        },
      });
      requestAnimationFrame(() => ScrollTrigger.refresh());
    });
  }; // initBanner

  /* -------------------------
     Video section play buttons
     ------------------------- */
  const initVideoSections = () => {
    if (!window.Playerjs) return;
    const sections = $$(".video-section");
    if (!sections.length) return;

    const players = Playerjs.createAll(sections);
    if (!players.length) return;

    const pauseIfOutOfView = () => {
      players.forEach((player) => player.pauseIfOutOfView());
    };

    window.addEventListener("scroll", pauseIfOutOfView, { passive: true });
  };

  /* -------------------------
     Why Axari section
     ------------------------- */
  const initWhyAxari = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    const whyAxariSection = $(".why-axari-section");
    if (!whyAxariSection) return;
    const cards = $$(".why-axari-section-card");
    const title = $(".why-axari-section-title");
    if (!title || !cards.length) return;

    gsap.set(title, { opacity: 0, y: 100 });
    cards.forEach((c, idx) => {
      const opt = { opacity: 0, y: 100 };
      if (idx % 2 === 0) Object.assign(opt, { x: -100, rotate: -10 });
      else Object.assign(opt, { x: 100, rotate: 10 });
      if (idx === cards.length - 1) Object.assign(opt, { rotate: -10 });
      gsap.set(c, opt);
    });

    const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
    tl.to(title, { opacity: 1, y: 0, duration: 0.5 });
    cards.forEach((c) =>
      tl.to(c, { opacity: 1, y: 0, x: 0, rotate: 0, duration: 0.5 })
    );

    ScrollTrigger.create({
      trigger: whyAxariSection,
      start: "top bottom-=40%",
      end: "+=120%",
      scrub: 0.5,
      animation: tl,
      invalidateOnRefresh: true,
    });
  };

  /* -------------------------
     Taskforce panels
     ------------------------- */
  const initTaskforce = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    const taskforceSection = $(".taskforce-section");
    if (!taskforceSection) return;
    const panels = gsap.utils.toArray(".taskforce-section-panel");
    if (!panels.length) return;

    // create unmute buttons for any panel videos up-front
    panels.forEach((panel) => {
      const videoEl = panel.querySelector(
        ".taskforce-section-panel-content-media-wrapper video"
      );
      if (videoEl) createUnmuteButton(videoEl);
    });

    // initial sets for content inside panels
    panels.forEach((panel) => {
      const title = panel.querySelector(
        ".taskforce-section-panel-content-title"
      );
      const info = panel.querySelector(".taskforce-section-panel-content-info");
      const media = panel.querySelector(
        ".taskforce-section-panel-content-media-wrapper"
      );
      const cards = panel.querySelector(
        ".taskforce-section-panel-content-cards"
      );
      const image = panel.querySelector(
        ".taskforce-section-panel-content-image-wrapper .taskforce-section-panel-content-image"
      );
      const imageAnimated = panel.querySelector(
        ".taskforce-section-panel-content-image-animated"
      );
      const underlay = panel.querySelector(".taskforce-section-image-underlay");
      if (title) {
        gsap.set(title, {
          autoAlpha: 0,
          yPercent: 0,
          xPercent: -50,
          transformOrigin: "50% 50%",
          color: title.dataset.color,
        });
      }
      if (info) gsap.set(info, { autoAlpha: 0, x: -100 });
      if (media) gsap.set(media, { autoAlpha: 0, x: 100 });
      if (cards) gsap.set(cards, { autoAlpha: 0, x: 100 });
      if (image)
        gsap.set(image, {
          autoAlpha: 0,
          yPercent: 100,
          scale: 0.9,
          transformOrigin: "0% 100%",
        });
      if (imageAnimated) gsap.set(imageAnimated, { autoAlpha: 0, x: -100 });
      if (underlay)
        gsap.set(underlay, {
          autoAlpha: 0,
          xPercent: 100,
          yPercent: 30,
          scale: 0,
        });
    });
    // prepare panelData
    const panelData = panels.map((panel) => {
      const mediaElement = panel.querySelector(
        ".taskforce-section-panel-content-media-wrapper"
      );
      const unmuteOverlay = mediaElement
        ? (
            mediaElement.closest(".taskforce-section-panel-content-media") ||
            mediaElement.parentElement
          )?.querySelector(".taskforce-unmute")
        : null;
      return {
        panel,
        info: panel.querySelector(".taskforce-section-panel-content-info"),
        mediaElement,
        unmuteOverlay,
        cards: panel.querySelector(".taskforce-section-panel-content-cards"),
        title: panel.querySelector(".taskforce-section-panel-content-title"),
        image: panel.querySelector(
          ".taskforce-section-panel-content-image-wrapper .taskforce-section-panel-content-image"
        ),
        imageAnimated: panel.querySelector(
          ".taskforce-section-panel-content-image-animated"
        ),
        underlay: panel.querySelector(".taskforce-section-image-underlay"),
        elements: [], // filled later if needed
      };
    });

    // builder for per-panel timeline
    const buildPanelTimeline = (idx) => {
      const pd = panelData[idx];
      const {
        title,
        image,
        imageAnimated,
        info,
        mediaElement,
        cards,
        underlay,
      } = pd;
      const tl = gsap.timeline({ paused: true });

      if (title || image) tl.add("phase1");
      if (title) {
        tl.to(
          title,
          {
            autoAlpha: 1,
            yPercent: -50,
            xPercent: -50,
            transformOrigin: "50% 50%",
            duration: 0.6,
            ease: "power3.out",
            overwrite: "auto",
          },
          "phase1"
        );
      }
      if (image) {
        tl.to(
          image,
          {
            autoAlpha: 1,
            yPercent: 0,
            duration: 0.9,
            ease: "power3.out",
            overwrite: "auto",
          },
          "phase1"
        );
      }
      if (title || image) tl.add("phase2", "+=0.05");
      if (title) {
        tl.to(
          title,
          {
            color: "#E5E5E5",
            duration: 0.75,
            ease: "power2.inOut",
            overwrite: "auto",
          },
          "phase2"
        );
      }
      if (underlay) {
        tl.to(
          underlay,
          {
            autoAlpha: 1,
            xPercent: 0,
            yPercent: -30,
            scale: 1,
            duration: 0.9,
            ease: "power3.out",
            overwrite: "auto",
            force3D: true,
          },
          "phase2"
        );
      }
      if (image) {
        tl.to(
          image,
          {
            scale: 1,
            duration: 1,
            ease: "power3.out",
            overwrite: "auto",
            force3D: true,
          },
          "phase2"
        );
      }
      const group = [imageAnimated, info, mediaElement, cards].filter(Boolean);
      if (group.length) {
        tl.to(
          group,
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.5,
            ease: "power3.out",
            stagger: 0.15,
            overwrite: "auto",
            force3D: true,
          },
          "-=0.5"
        );
      }

      pd.tl = tl;
      return tl;
    };

    let currentIndex = -1;

    const showPanel = (idx) => {
      if (!panelData[idx]) return;
      const { panel, mediaElement, unmuteOverlay } = panelData[idx];
      gsap.fromTo(
        panel,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.5, ease: "power2.out", overwrite: "auto" }
      );
      if (panelData[idx].tl) {
        panelData[idx].tl.kill();
        panelData[idx].tl = null;
      }
      const tl = buildPanelTimeline(idx);
      tl.timeScale(1).play(0);
      panels.forEach((p) => p.classList.remove("is-active"));
      panel.classList.add("is-active");

      if (mediaElement && unmuteOverlay) {
        const videoEl = mediaElement.querySelector("video");
        // mute others
        $$(".taskforce-section-panel-content-media-element").forEach((v) => {
          if (v !== videoEl) {
            v.muted = true;
            v.setAttribute("muted", "");
          }
        });
        unmuteOverlay.classList.remove("hidden");
        const svgMuted = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000" class="bi bi-volume-mute" viewBox="0 0 16 16"><path d="M6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06M6 5.04 4.312 6.39A.5.5 0 0 1 4 6.5H2v3h2a.5.5 0 0 1 .312.11L6 10.96zm7.854.606a.5.5 0 0 1 0 .708L12.207 8l1.647 1.646a.5.5 0 0 1-.708.708L11.5 8.707l-1.646 1.647a.5.5 0 0 1-.708-.708L10.793 8 9.146 6.354a.5.5 0 1 1 .708-.708L11.5 7.293l1.646-1.647a.5.5 0 0 1 .708 0"/></svg>`;
        const svgUnmuted = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000" class="bi bi-volume-up" viewBox="0 0 16 16"><path d="M11.536 14.01A8.47 8.47 0 0 0 14.026 8a8.47 8.47 0 0 0-2.49-6.01l-.708.707A7.48 7.48 0 0 1 13.025 8c0 2.071-.84 3.946-2.197 5.303z"/><path d="M10.121 12.596A6.48 6.48 0 0 0 12.025 8a6.48 6.48 0 0 0-1.904-4.596l-.707.707A5.48 5.48 0 0 1 11.025 8a5.48 5.48 0 0 1-1.61 3.89z"/><path d="M10.025 8a4.5 4.5 0 0 1-1.318 3.182L8 10.475A3.5 3.5 0 0 0 9.025 8c0-.966-.392-1.841-1.025-2.475l.707-.707A4.5 4.5 0 0 1 10.025 8M7 4a.5.5 0 0 0-.812-.39L3.825 5.5H1.5A.5.5 0 0 0 1 6v4a.5.5 0 0 0 .5.5h2.325l2.363 1.89A.5.5 0 0 0 7 12zM4.312 6.39 6 5.04v5.92L4.312 9.61A.5.5 0 0 0 4 9.5H2v-3h2a.5.5 0 0 0 .312-.11"/></svg>`;
        unmuteOverlay.innerHTML =
          videoEl && videoEl.muted !== false ? svgMuted : svgUnmuted;
      }
    };

    const hidePanel = (idx) => {
      if (!panelData[idx]) return;
      const { panel, mediaElement, unmuteOverlay } = panelData[idx];
      gsap.to(panel, {
        autoAlpha: 0,
        duration: 0.4,
        ease: "power2.in",
        overwrite: "auto",
      });
      const tl = panelData[idx].tl || buildPanelTimeline(idx);
      tl.timeScale(1.5).reverse();
      if (mediaElement) {
        const videoEl = mediaElement.querySelector("video");
        if (videoEl) {
          videoEl.muted = true;
          videoEl.setAttribute("muted", "");
        }
        if (unmuteOverlay) {
          const svgMuted = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000" class="bi bi-volume-mute" viewBox="0 0 16 16"><path d="M6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06M6 5.04 4.312 6.39A.5.5 0 0 1 4 6.5H2v3h2a.5.5 0 0 1 .312.11L6 10.96zm7.854.606a.5.5 0 0 1 0 .708L12.207 8l1.647 1.646a.5.5 0 0 1-.708.708L11.5 8.707l-1.646 1.647a.5.5 0 0 1-.708-.708L10.793 8 9.146 6.354a.5.5 0 1 1 .708-.708L11.5 7.293l1.646-1.647a.5.5 0 0 1 .708 0"/></svg>`;
          unmuteOverlay.innerHTML = svgMuted;
          unmuteOverlay.classList.remove("hidden");
        }
      }
      panel.classList.remove("is-active");
    };

    const introTitle = taskforceSection.querySelector(
      ".taskforce-section-title"
    );
    const introImage = taskforceSection.querySelector(
      ".taskforce-section-image-main"
    );
    const introEve = taskforceSection.querySelector(
      ".taskforce-section-image-eve"
    );

    gsap.set([introTitle, introImage, introEve], { autoAlpha: 0, y: 100 });

    const introInTL = gsap.timeline({
      scrollTrigger: {
        trigger: taskforceSection,
        start: "top top+=50%",
        end: "bottom top+=50%",
        scrub: 0.4,
        pin: false,
        invalidateOnRefresh: true,
        refreshPriority: 2,
      },
    });

    introInTL
      .to(introTitle, {
        autoAlpha: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
      })
      .to(
        [introImage, introEve],
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          force3D: true,
        },
        "-=0.2"
      )
      .to(
        introImage,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          force3D: true,
        },
        "-=0.8"
      )
      .to(
        introEve,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          force3D: true,
        },
        "-=0.8"
      );

    const introOutTL = gsap.timeline({
      scrollTrigger: {
        trigger: taskforceSection,
        start: "top+=80% top",
        end: "bottom top",
        scrub: 0.4,
        pin: false,
        invalidateOnRefresh: true,
        refreshPriority: 2,
      },
    });

    introOutTL
      .to(introTitle, {
        autoAlpha: 0,
        y: -50,
        duration: 0.6,
        ease: "power2.inOut",
      })
      .to(introImage, {
        autoAlpha: 0,
        duration: 0.6,
        ease: "power2.inOut",
        force3D: true,
        overwrite: "auto",
      })
      .to(
        introEve,
        {
          autoAlpha: 1,
          y: 0,
          x: "50%",
          scale: 1.4,
          transformOrigin: "0% 100%",
          duration: 1.2,
          ease: "power3.out",
          force3D: true,
          overwrite: "auto",
        },
        "<"
      );
    // create pin
    const pinST = ScrollTrigger.create({
      trigger: taskforceSection,
      start: "top top",
      end: () => "+=" + panels.length * window.innerHeight,
      pin: true,
      anticipatePin: 1,
      onUpdate: (self) => {
        const total = panels.length;
        if (total <= 1) return;
        const introShare = 1 / total;
        const p = (self.progress - introShare) / (1 - introShare);
        if (p <= 0) {
          if (currentIndex >= 0) {
            hidePanel(currentIndex);
            currentIndex = -1;
          }
          panels.forEach((pnl, i) => {
            if (i === 0) {
              pnl.classList.add("is-active");
              gsap.set(pnl, { autoAlpha: 1 });
            } else {
              pnl.classList.remove("is-active");
            }
          });
          return;
        }
        const contentPanels = total - 1;
        const contentIdx =
          contentPanels > 0
            ? Math.min(
                contentPanels - 1,
                Math.max(0, Math.floor(p * contentPanels))
              )
            : 0;
        const actualIdx = contentIdx + 1;
        if (introImage && p > 0) gsap.set(introImage, { autoAlpha: 0 });
        if (introTitle && p > 0) gsap.set(introTitle, { autoAlpha: 0 });
        if (introEve) {
          if (actualIdx >= 2)
            gsap.to(introEve, {
              autoAlpha: 0,
              duration: 0.3,
              ease: "power2.out",
            });
          else
            gsap.to(introEve, {
              autoAlpha: 1,
              duration: 0.3,
              ease: "power2.out",
            });
        }
        if (actualIdx !== currentIndex) {
          let targetIdx = actualIdx;
          if (currentIndex >= 0) {
            if (targetIdx > currentIndex + 1) targetIdx = currentIndex + 1;
            if (targetIdx < currentIndex - 1) targetIdx = currentIndex - 1;
          }
          if (currentIndex >= 0) hidePanel(currentIndex);
          showPanel(targetIdx);
          currentIndex = targetIdx;
        }
      },
    });

    requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  /* -------------------------
     Benefits / CTA / Footer animations (smaller)
     ------------------------- */
  const initBenefits = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    const benefitsSection = $(".benefits-section");
    if (!benefitsSection) return;
    const title = $(".benefits-section-title");
    const cards = $$(".benefits-section-card");
    const subtitle = $(".benefits-section-subtitle");

    const initialTargets = [];
    if (title) initialTargets.push(title);
    if (cards.length) initialTargets.push(...cards);
    if (subtitle) initialTargets.push(subtitle);
    if (initialTargets.length) gsap.set(initialTargets, { opacity: 0, y: 100 });

    ScrollTrigger.create({
      trigger: benefitsSection,
      start: "top bottom-=30%",
      end: "+=75%",
      scrub: 1,
      onEnter: () => {
        const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
        if (title) tl.to(title, { opacity: 1, y: 0, duration: 0.5 });
        if (cards.length)
          tl.to(
            cards,
            { opacity: 1, y: 0, stagger: 0.1, duration: 0.5 },
            "-=0.3"
          );
        if (subtitle)
          tl.to(subtitle, { opacity: 1, y: 0, duration: 0.5 }, "-=0.3");
      },
    });
  };

  /* -------------------------
     Boot sequence
     ------------------------- */
  const boot = () => {
    // init video sections (play buttons)
    initVideoSections();
    // create unmute overlays for taskforce panel videos (done inside initTaskforce)
    // mobile fallback may stop heavy execution; if it returns true, leave early
    const stopped = mobileFallback();
    if (stopped) {
      releaseScrollLock();
      return;
    }

    // initialize gsap-based animations if available
    try {
      initBanner();
      initWhyAxari();
      initTaskforce();
      initBenefits();
    } catch (e) {
      // if GSAP is missing or errors happen, don't break the page
      console.warn("Animation init error:", e);
    }

    // small safe resize handler: refresh ScrollTrigger on resize (debounced)
    const safeRefresh = debounce(() => {
      try {
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      } catch (e) {}
    }, 120);
    window.addEventListener("resize", safeRefresh);
    // also refresh on orientation change quickly
    window.addEventListener("orientationchange", safeRefresh);

    let resizeReloadPending = false;
    let resizeReloadTimer = null;
    const handleViewportReload = () => {
      if (resizeReloadPending) return;
      resizeReloadPending = true;
      if (resizeReloadTimer) clearTimeout(resizeReloadTimer);
      resizeReloadTimer = setTimeout(() => {
        window.location.reload();
      }, 250);
    };
    window.addEventListener("resize", handleViewportReload);
    window.addEventListener("orientationchange", handleViewportReload);

    // a final rAF refresh
    releaseScrollLockWhenReady();
  };

  // run on load (keeps parity with original behavior)
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    // delay slightly if needed so webfonts/layout settled
    window.setTimeout(boot, 0);
  } else {
    window.addEventListener("load", boot);
  }
})();
